/** Visual freshness decay. A stale report must visibly die, not silently linger. */

import { HAZARDS, type HazardType, type Severity } from "./hazards.js";

export type DecayState = {
  /** Fraction of lifetime elapsed, clamped to [0, 1+]. */
  t: number;
  opacity: number;
  /** Multiplier on the severity's base stroke width, floored later at 2px. */
  strokeScale: number;
  /** Multiplier on the pin diameter. */
  sizeScale: number;
  /** Remaining sweep of the countdown ring, degrees. */
  ringDegrees: number;
  pulses: boolean;
  /** Severity actually rendered — pins de-escalate as they age. */
  effectiveSeverity: Severity;
  expired: boolean;
};

export function decayState(
  type: HazardType, createdAtMs: number, expiresAtMs: number, nowMs: number,
): DecayState {
  const span = Math.max(1, expiresAtMs - createdAtMs);
  const t = Math.max(0, (nowMs - createdAtMs) / span);
  const base = HAZARDS[type].severity;

  let opacity: number;
  if (t <= 0.2) opacity = 1;
  else if (t <= 1) opacity = 1 - 0.75 * Math.pow((t - 0.2) / 0.8, 1.4);
  else opacity = Math.max(0, 0.25 * (1 - (nowMs - expiresAtMs) / 60_000)); // 60s fade-out

  const sizeScale = t <= 0.2 ? 1 : Math.max(0.78, 1 - 0.22 * ((t - 0.2) / 0.8));

  // Pins de-escalate visibly: pulse stops at half life, rrezik drops its inversion at 0.6.
  const pulses = t < 0.5;
  const effectiveSeverity: Severity =
    base === "rrezik" && t >= 0.6 ? "kujdes" : base;

  return {
    t,
    opacity,
    strokeScale: Math.max(0.45, opacity),
    sizeScale,
    ringDegrees: Math.max(0, 360 * (1 - Math.min(1, t))),
    pulses,
    effectiveSeverity,
    expired: nowMs >= expiresAtMs + 60_000,
  };
}

export const SEVERITY_STYLE: Record<Severity, {
  diameterPt: number; strokePx: number; inverted: boolean; pulseHz: number;
}> = {
  info:   { diameterPt: 40, strokePx: 3, inverted: false, pulseHz: 0 },
  kujdes: { diameterPt: 52, strokePx: 5, inverted: false, pulseHz: 0.5 },
  rrezik: { diameterPt: 64, strokePx: 7, inverted: true,  pulseHz: 1.2 },
};
